/**
 * C4 · add-employee view-model — pure display/shaping helpers kept out of the
 * .vue so node:test can cover the logic directly (the companies repo has no SFC
 * test runner). Covers form validation (§2/§3 rules: national id = 14 digits,
 * required fields, email shape), payload shaping for POST /employees, bilingual
 * entity naming and the status → tag-class map. Plain CommonJS.
 */
var STATUS_TAG = {
  active: 'ok',
  suspended: 'muted',
  left: 'danger',
};

var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
var NID_RE = /^\d{14}$/;
// Egyptian NID position 1 encodes birth century: 2 = 1900s, 3 = 2000s. Any other
// digit there means the 14-digit string can't be a real Egyptian NID even though
// it passes the format-only NID_RE — a plausibility check, not a full checksum.
var NID_CENTURY_RE = /^[23]/;
// Egyptian mobile numbers: 01 + 9 digits (011/010/012/015 network prefixes) = 11
// digits total. No other screen in this app validates a phone number today (only
// this file's own non-empty check), so this is a fresh regex, not a reused one.
var MOBILE_RE = /^01\d{9}$/;

/** Bilingual entity label — nameAr under ar, nameEn (fallback nameAr) under en. */
function entityName(entity, locale) {
  if (!entity) return '';
  if (locale === 'en') return entity.nameEn || entity.nameAr || '';
  return entity.nameAr || entity.nameEn || '';
}

/** status → .tag modifier class. */
function statusTag(status) {
  return STATUS_TAG[status] || 'muted';
}

/**
 * F1: is the tier (contractEntityId) required? Yes whenever the contract tree
 * offers at least one tier. Only a tree that LOADED with zero tiers (opts.tierCount
 * === 0 — e.g. live POL-2026-00512 / 00417) lets HR add without one: the backend
 * DTO treats it as optional and the Excel import already accepts tier-less rows
 * (TIER_NOT_FOUND warning). An unknown count (tree not loaded / fetch failed —
 * opts.loadFailed) fails closed → still required.
 */
function isTierRequired(opts) {
  return !(opts && opts.tierCount === 0);
}

/**
 * F1-r2: should a stale tier error be dropped? HR can submit before the tree
 * loads (unknown count → required → errors.contractEntityId = 'required'); if the
 * tree then loads with ZERO tiers that error no longer applies and would hide the
 * no-tiers hint. True only when such an error exists AND the tier is now optional.
 */
function shouldClearTierError(errors, opts) {
  return !!(errors && errors.contractEntityId) && !isTierRequired(opts);
}

/**
 * F1-r2: which i18n hint (under `employee-add.`) sits below the tier select, or
 * null when the blocking error line replaces it. A failed tree fetch always
 * explains itself — even next to a 'required' error, which HR can't satisfy from
 * an empty dropdown.
 */
function tierHintKey(opts, hasError) {
  if (opts && opts.loadFailed) return 'tiersLoadFailedHint';
  if (hasError) return null;
  return isTierRequired(opts) ? 'tierHint' : 'noTiersHint';
}

/**
 * Validate the reactive form. Returns { valid, errors, warnings }:
 *  - errors maps a field name to a BLOCKING error code
 *    ('required' | 'nid' | 'email') the view turns into an i18n message and
 *    that stops submission (`valid` is false while any exist).
 *  - warnings maps a field name to a NON-blocking, visible-only code
 *    ('nidCentury' | 'phoneFormat') — plausibility checks that flag likely-bad
 *    data without stopping a save the same way `errors` does.
 * `opts.tierCount` = how many tiers the loaded contract tree offers (see
 * isTierRequired above); omit it when the tree hasn't loaded.
 */
function validate(form, opts) {
  var f = form || {};
  var errors = {};
  var warnings = {};

  if (!String(f.fullName == null ? '' : f.fullName).trim()) errors.fullName = 'required';

  var nid = String(f.nationalId == null ? '' : f.nationalId).trim();
  if (!nid) errors.nationalId = 'required';
  else if (!NID_RE.test(nid)) errors.nationalId = 'nid';
  else if (!NID_CENTURY_RE.test(nid)) warnings.nationalId = 'nidCentury';

  var phone = String(f.phone == null ? '' : f.phone).trim();
  if (!phone) errors.phone = 'required';
  else if (!MOBILE_RE.test(phone)) warnings.phone = 'phoneFormat';

  if (!String(f.cardNumber == null ? '' : f.cardNumber).trim()) errors.cardNumber = 'required';

  // S10/M12 (Vivo ببساطة Oct-4): the employee's TIER (الفئة = a contract-tree
  // entity) is REQUIRED when adding an employee in the dashboard ("must set") —
  // unless the tree offers zero tiers (F1, see isTierRequired).
  if (isTierRequired(opts) && !String(f.contractEntityId == null ? '' : f.contractEntityId).trim())
    errors.contractEntityId = 'required';

  var email = String(f.email == null ? '' : f.email).trim();
  if (email && !EMAIL_RE.test(email)) errors.email = 'email';

  return { valid: Object.keys(errors).length === 0, errors: errors, warnings: warnings };
}

/**
 * Shape the POST /employees body: required fields (trimmed) + status default
 * 'active', dropping any empty optional so the backend never sees blank strings.
 */
function buildPayload(form) {
  var f = form || {};
  var payload = {
    fullName: String(f.fullName == null ? '' : f.fullName).trim(),
    nationalId: String(f.nationalId == null ? '' : f.nationalId).trim(),
    // §4 CreateEmployeeDto names this `mobile`; the form model calls it `phone`.
    mobile: String(f.phone == null ? '' : f.phone).trim(),
    cardNumber: String(f.cardNumber == null ? '' : f.cardNumber).trim(),
    status: f.status || 'active',
  };
  // Optionals the §4 contract whitelists. gender/hireDate/policyId were added to
  // CreateEmployeeDto (governed A3 extension — the Prisma columns already existed);
  // each is dropped when empty so the backend never sees a blank string.
  // S10/M12: contractEntityId (tier) rides the same drop-when-empty path; it is
  // required by validate() above whenever the tree offers a tier, so it is only
  // omitted for a tier-less tree (F1).
  var optional = ['birthDate', 'contractEntityId', 'deptId', 'email', 'gender', 'hireDate', 'policyId'];
  for (var i = 0; i < optional.length; i++) {
    var key = optional[i];
    var val = f[key];
    if (val === undefined || val === null) continue;
    if (typeof val === 'string') {
      var trimmed = val.trim();
      if (trimmed !== '') payload[key] = trimmed;
    } else {
      payload[key] = val;
    }
  }
  return payload;
}

module.exports = {
  STATUS_TAG: STATUS_TAG,
  entityName: entityName,
  statusTag: statusTag,
  isTierRequired: isTierRequired,
  shouldClearTierError: shouldClearTierError,
  tierHintKey: tierHintKey,
  validate: validate,
  buildPayload: buildPayload,
};
